import { useEffect, useState } from "react";
import { Download, Smartphone, Check, AlertTriangle } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { usePwaInstall } from "@/lib/use-pwa-install";

/**
 * "Install Finally as an app" block on /me/account.
 *
 * Three states by platform:
 *   - Android Chrome / desktop Chrome / Edge → we get `beforeinstallprompt`
 *     and show a single "Install" button that opens the native dialog.
 *   - iOS Safari → no event fires, so we show illustrated steps with
 *     stylised pictures of the Safari toolbar and Share sheet. The first
 *     thing we surface is an "are you actually in Safari?" check, because
 *     opening the link from inside Telegram/Instagram/Slack puts the user
 *     in an in-app browser that doesn't expose Add-to-Home-Screen at all.
 *   - Other browsers (Firefox desktop, etc.) → quiet fallback explaining
 *     that they can bookmark the site for now.
 *
 * If we detect the page is already running in standalone (installed PWA),
 * we render nothing — the block has done its job.
 */

/** True if the page is rendered inside an in-app browser (Telegram /
 *  Instagram / Facebook / TikTok / etc.) on iOS. These webviews intentionally
 *  hide Add-to-Home-Screen — the user has to bounce out to real Safari.
 *
 *  Detection strategy: explicit UA stamps for the chatty apps (Facebook,
 *  Instagram, Line, TikTok, Twitter, WeChat, LinkedIn). Telegram iOS does
 *  NOT stamp anything — its in-app browser pretends to be Safari. To catch
 *  that case we look for a Safari UA that is *missing* the `Version/x.x`
 *  token — real mobile Safari always emits it, every WKWebView-based in-app
 *  browser (Telegram included) omits it. */
function detectInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;

  // 1) Apps that helpfully stamp themselves.
  if (
    /(FBAN|FBAV|FB_IAB|Instagram|Line|MicroMessenger|Twitter|TikTok|Telegram|Snapchat|LinkedInApp)/i.test(
      ua,
    )
  ) {
    return true;
  }

  // 2) iOS WKWebView fingerprint — Safari token but no Version/x.x.
  //    Catches Telegram, X/Twitter in-app, Discord, Threads, Bluesky webview.
  const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) {
    const looksLikeSafari = /Version\/[\d.]+ +Mobile\/[\w.]+ +Safari/.test(ua);
    if (!looksLikeSafari) return true;
  }

  return false;
}

export function InstallAppBlock() {
  const { canPrompt, device, isStandalone, promptInstall } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);
  const [inApp, setInApp] = useState(false);
  const [result, setResult] = useState<"accepted" | "dismissed" | null>(null);

  useEffect(() => {
    setInApp(detectInAppBrowser());
  }, []);

  if (isStandalone) return null;

  async function onInstallClick() {
    const outcome = await promptInstall();
    if (outcome) setResult(outcome);
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(window.location.origin + "/me/account");
    } catch {
      /* ignore — fallback hint is good enough */
    }
  }

  return (
    <Card padding="lg" className="mt-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#dbeafe] text-[#2563eb]">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
            Install
          </p>
          <p className="mt-1 text-[16px] font-semibold text-[#1a1a1a]">
            Add Finally to your home screen
          </p>
          <p className="mt-1 text-[13px] text-[#6c757d]">
            Open the app in one tap, just like a native one. Works without an
            app store install.
          </p>
        </div>
      </div>

      {/* Android / desktop Chrome — native prompt */}
      {canPrompt ? (
        <div className="mt-4">
          <Button onClick={onInstallClick} leadingIcon={<Download className="h-4 w-4" />}>
            Install Finally
          </Button>
          {result === "dismissed" ? (
            <p className="mt-3 text-[12px] text-[#94a3b8]">
              No problem — you can install it later from this same place.
            </p>
          ) : null}
          {result === "accepted" ? (
            <p className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#16a34a]">
              <Check className="h-3.5 w-3.5" /> Installed — check your home screen.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* iOS — manual gesture, no prompt event available */}
      {!canPrompt && device === "ios" ? (
        <div className="mt-4">
          {/* If we're inside Telegram/Instagram/etc., the user MUST escape to
              Safari first — Add-to-Home-Screen is hidden inside in-app
              browsers. Surface this loud at the top. */}
          {inApp ? (
            <div className="mb-4 rounded-[12px] border border-[#fed7aa] bg-[#fffbeb] p-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#d97706]" />
                <div className="min-w-0 text-[13px] leading-relaxed text-[#78350f]">
                  <p className="font-bold">You're inside another app's browser.</p>
                  <p className="mt-1">
                    iPhone hides Add-to-Home-Screen here. You need to bounce
                    out to real Safari first:
                  </p>
                  <ol className="mt-2 ml-4 list-decimal space-y-1">
                    <li>
                      Tap the <strong>···</strong> button (top-right corner).
                    </li>
                    <li>
                      Pick <strong>Open in Safari</strong>{" "}
                      <span className="text-[#92400e]">/ Открыть в Safari</span>{" "}
                      from the menu.
                    </li>
                    <li>
                      You'll need to sign in again — sessions don't carry over.
                      After sign-in come back to <em>Account</em>.
                    </li>
                  </ol>
                  <button
                    type="button"
                    onClick={copyUrl}
                    className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md border border-[#d97706]/40 bg-white px-3 text-[12px] font-semibold text-[#78350f] hover:bg-[#fef3c7]"
                  >
                    Copy this link
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setIosOpen((v) => !v)}
            className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#e2e8f0] bg-white px-4 text-[13px] font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]"
          >
            {iosOpen ? "Hide steps" : "Show me how (iPhone / iPad)"}
          </button>

          {iosOpen ? (
            <div className="mt-5 flex flex-col gap-5">
              <IosStep
                n={1}
                title="Open the Share menu"
                text={
                  <>
                    Two paths, depending on your Safari layout:
                    <br />
                    <strong>If you see the panel with arrows + Share icon at the bottom</strong> —
                    tap the <span className="inline-block">⬆️</span> Share button there.
                    <br />
                    <strong>If you only see an address bar with a "···" button</strong> —
                    tap the <strong>···</strong>, then pick <strong>Share</strong>{" "}
                    <span className="text-[#94a3b8]">/ Поделиться</span> from the menu.
                  </>
                }
                illustration={<SafariShareEntry />}
              />
              <IosStep
                n={2}
                title="Pull the share sheet up, then pick 'Add to Home Screen'"
                text='Swipe up on the share sheet to expand it (or tap "Show More"). Find the row with the + icon — labelled "Add to Home Screen" in English or "На экран «Домой»" in Russian.'
                illustration={<ShareSheetIllustration />}
              />
              <IosStep
                n={3}
                title="Tap Add"
                text='Confirm the name "Finally" and tap "Add" in the top-right corner. The icon lands on your home screen.'
                illustration={<AddConfirmIllustration />}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Browsers that don't expose an install path — quiet fallback */}
      {!canPrompt && device !== "ios" ? (
        <p className="mt-4 text-[12px] text-[#94a3b8]">
          Your browser doesn't expose an install button. You can bookmark
          this page for quick access, or open Finally in Chrome / Edge / Safari
          for the full home-screen experience.
        </p>
      ) : null}
    </Card>
  );
}

function IosStep({
  n,
  title,
  text,
  illustration,
}: {
  n: number;
  title: string;
  text: React.ReactNode;
  illustration: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#dbeafe] text-[12px] font-bold text-[#2563eb]">
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-bold text-[#1a1a1a]">{title}</p>
        <div className="mt-0.5 text-[12.5px] leading-relaxed text-[#475569]">{text}</div>
        <div className="mt-2.5">{illustration}</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Illustrations: stylised mini-screens of the real iOS Safari UI.
 * Done as inline SVG so they scale crisply without an extra request.
 * ──────────────────────────────────────────────────────────────── */

/** Two stacked mock-ups, side by side, for the two possible Safari layouts:
 *   - The classic bottom toolbar with the Share icon (older iOS / iPad / users
 *     who explicitly set Safari to the multi-button mode).
 *   - The compact Single-Tab layout (iOS 17/18 default on iPhone) where the
 *     bottom is just the address bar + ⋯ menu — Share is *inside* the menu. */
function SafariShareEntry() {
  return (
    <div className="flex flex-col gap-2">
      {/* Layout A — classic bottom toolbar with Share button highlighted */}
      <div className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-[#f1f5f9] p-2.5">
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
          Layout A · bottom toolbar
        </p>
        <svg viewBox="0 0 280 60" className="block h-auto w-full" aria-hidden="true">
          <rect x="4" y="8" width="272" height="46" rx="14" fill="#ffffff" stroke="#e2e8f0" />
          <path d="M28 31 L20 31 M20 31 L24 27 M20 31 L24 35"
                stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <path d="M52 31 L60 31 M60 31 L56 27 M60 31 L56 35"
                stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <rect x="110" y="11" width="60" height="40" rx="10" fill="#dbeafe" />
          <path d="M140 18 L140 36 M140 18 L134 24 M140 18 L146 24"
                stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <path d="M128 30 L128 42 L152 42 L152 30"
                stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <path d="M196 22 L196 42 L204 36 L212 42 L212 22 Z"
                stroke="#475569" strokeWidth="2" strokeLinejoin="round" fill="none"/>
          <rect x="240" y="22" width="14" height="18" rx="2" stroke="#475569" strokeWidth="2" fill="none"/>
          <rect x="244" y="18" width="14" height="18" rx="2" stroke="#475569" strokeWidth="2" fill="none"/>
        </svg>
      </div>

      {/* Layout B — Single-Tab compact bottom, Share lives inside "···" menu */}
      <div className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-[#f1f5f9] p-2.5">
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
          Layout B · single-tab bottom (default on iOS 17/18)
        </p>
        <svg viewBox="0 0 280 60" className="block h-auto w-full" aria-hidden="true">
          {/* Back chevron pill */}
          <circle cx="22" cy="31" r="16" fill="#ffffff" stroke="#e2e8f0"/>
          <path d="M26 25 L18 31 L26 37"
                stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          {/* Address bar pill */}
          <rect x="52" y="15" width="172" height="32" rx="16" fill="#ffffff" stroke="#e2e8f0"/>
          <text x="76" y="35" fontFamily="-apple-system, sans-serif" fontSize="12" fontWeight="500" fill="#1a1a1a">meetup.finally.plus</text>
          {/* Refresh icon inside pill */}
          <path d="M204 28 a8 8 0 1 1 -2 5 M204 22 L204 28 L210 28"
                stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          {/* "···" menu pill — HIGHLIGHTED, this is the new tap target */}
          <circle cx="258" cy="31" r="16" fill="#dbeafe" stroke="#2563eb" strokeWidth="2"/>
          <circle cx="251" cy="31" r="1.8" fill="#2563eb"/>
          <circle cx="258" cy="31" r="1.8" fill="#2563eb"/>
          <circle cx="265" cy="31" r="1.8" fill="#2563eb"/>
          {/* "Tap here" pointer under "···" */}
          <path d="M258 58 L258 52"
                stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="258" cy="58" r="2.5" fill="#2563eb" />
        </svg>
        <p className="mt-2 text-[11px] leading-relaxed text-[#475569]">
          Tap <strong>···</strong> → menu opens → pick <strong>Share</strong>{" "}
          <span className="text-[#94a3b8]">/ Поделиться</span>. Then continue with step 2.
        </p>
      </div>
    </div>
  );
}

/** Mock-up of the iOS Share sheet with "Add to Home Screen" highlighted. */
function ShareSheetIllustration() {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-[#f1f5f9] p-2.5">
      <svg viewBox="0 0 280 180" className="block h-auto w-full" aria-hidden="true">
        {/* Sheet background */}
        <rect x="6" y="6" width="268" height="168" rx="16" fill="#ffffff" stroke="#e2e8f0"/>
        {/* Header row */}
        <text x="20" y="30" fontFamily="-apple-system, sans-serif" fontSize="13" fontWeight="700" fill="#1a1a1a">Finally for Events</text>
        <text x="20" y="46" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#94a3b8">meetup.finally.plus</text>
        {/* Top apps row (greyed out) */}
        <g>
          <rect x="20" y="58" width="44" height="44" rx="10" fill="#f1f5f9"/>
          <rect x="74" y="58" width="44" height="44" rx="10" fill="#f1f5f9"/>
          <rect x="128" y="58" width="44" height="44" rx="10" fill="#f1f5f9"/>
          <rect x="182" y="58" width="44" height="44" rx="10" fill="#f1f5f9"/>
        </g>
        {/* "Add to Home Screen" row — highlighted */}
        <rect x="14" y="120" width="252" height="46" rx="10" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5"/>
        {/* Plus icon */}
        <g transform="translate(28 134)">
          <rect width="20" height="20" rx="5" fill="#ffffff" stroke="#2563eb" strokeWidth="1.5"/>
          <path d="M10 6 L10 14 M6 10 L14 10" stroke="#2563eb" strokeWidth="2" strokeLinecap="round"/>
        </g>
        <text x="58" y="148" fontFamily="-apple-system, sans-serif" fontSize="13" fontWeight="700" fill="#1e3a8a">Add to Home Screen</text>
        {/* Arrow chevron on right */}
        <path d="M250 138 L256 144 L250 150"
              stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    </div>
  );
}

/** Mock-up of the "Add to Home Screen" confirmation panel with the Add
 *  button in the top-right highlighted. */
function AddConfirmIllustration() {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-[#f1f5f9] p-2.5">
      <svg viewBox="0 0 280 130" className="block h-auto w-full" aria-hidden="true">
        {/* Background panel */}
        <rect x="6" y="6" width="268" height="118" rx="14" fill="#ffffff" stroke="#e2e8f0"/>
        {/* Top bar: Cancel | Add to Home Screen title | Add */}
        <text x="20" y="28" fontFamily="-apple-system, sans-serif" fontSize="12" fill="#475569">Cancel</text>
        <text x="140" y="28" textAnchor="middle" fontFamily="-apple-system, sans-serif" fontSize="13" fontWeight="700" fill="#1a1a1a">Add to Home Screen</text>
        {/* Add button — highlighted pill */}
        <rect x="222" y="14" width="42" height="22" rx="11" fill="#2563eb"/>
        <text x="243" y="29" textAnchor="middle" fontFamily="-apple-system, sans-serif" fontSize="12" fontWeight="700" fill="#ffffff">Add</text>
        {/* Pulse halo around Add */}
        <rect x="218" y="10" width="50" height="30" rx="15" fill="none" stroke="#2563eb" strokeOpacity="0.35" strokeWidth="2"/>
        {/* Divider */}
        <line x1="14" y1="46" x2="266" y2="46" stroke="#e2e8f0"/>
        {/* Icon + name preview */}
        <rect x="20" y="62" width="44" height="44" rx="10" fill="#2563eb"/>
        <g fill="#ffffff" transform="translate(20 62) scale(0.085)">
          <rect x="148" y="112" width="64" height="288" rx="14"/>
          <rect x="148" y="112" width="216" height="64" rx="14"/>
          <rect x="148" y="226" width="172" height="56" rx="12"/>
        </g>
        <rect x="76" y="66" width="180" height="16" rx="4" fill="#f1f5f9"/>
        <text x="84" y="78" fontFamily="-apple-system, sans-serif" fontSize="12" fontWeight="600" fill="#1a1a1a">Finally</text>
        <rect x="76" y="88" width="180" height="14" rx="4" fill="#f8fafc"/>
        <text x="84" y="99" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#94a3b8">meetup.finally.plus</text>
      </svg>
    </div>
  );
}
